/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import React, { FC, ReactNode, memo } from 'react'
import { rpMediaQueries } from '../../core/theme'

type Variant = 'default' | 'text'

interface BtnProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  /** Текст кнопки */
  txt?: string
  /** Обработчик клика без аргументов */
  onClick?: () => void
  /** Вариант отображения */
  variant?: Variant
  /** Растянуть на всю ширину контейнера */
  fullWidth?: boolean
  /** Заблокирована ли кнопка */
  disabled?: boolean
  children?: ReactNode
}

export interface BtnGoldProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  /** Текст кнопки */
  txt?: string
  /** Обработчик клика без аргументов */
  onClick?: () => void
  /** Растянуть на всю ширину контейнера */
  fullWidth?: boolean
  /** Заблокирована ли кнопка */
  disabled?: boolean
  children?: ReactNode
}

const StyledButton = styled.button<{
  variant: Variant
  fullWidth: boolean
  disabled: boolean
}>`
  padding: 7px;
  border-radius: 16px;
  text-align: center;
  display: inline-grid;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  // text-transform: lowercase;
  white-space: nowrap;

  ${(p) =>
    p.disabled &&
    `
    opacity: 0.27;
    cursor: not-allowed;
  `}

  ${rpMediaQueries.tablet} {
    padding: 12px 15px;
    border-radius: 30px;
  }

  ${(p) =>
    p.variant === 'text' &&
    `
    border: none;
    background: transparent;
  `}

  ${(p) =>
    p.variant === 'text' &&
    !p.disabled &&
    `
    &:hover {
      opacity: 0.7;
    }
  `}

  ${(p) =>
    p.variant === 'default' &&
    `
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.5);
    box-shadow:
      0px 12px 14px 0px rgba(0, 0, 0, 0.6),
      0px 1px 0px 0px rgba(0, 0, 0, 0.05),
      0px 4px 4px 0px rgba(0, 0, 0, 0.05),
      0px 8px 10px 0px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(7px);
  `}

  ${(p) =>
    p.variant === 'default' &&
    !p.disabled &&
    `
    &:hover {
      background: rgba(0, 0, 0, 0.7);
      border-color: rgba(255, 255, 255, 0.2);
      box-shadow:
        0px 12px 14px 0px rgba(0, 0, 0, 0.6),
        0px 1px 0px 0px rgba(0, 0, 0, 0.05),
        0px 4px 4px 0px rgba(0, 0, 0, 0.05),
        0px 8px 10px 0px rgba(0, 0, 0, 0.1),
        0 0 15px 0 rgba(255, 255, 255, 0.1);
    }
  `}

  ${(p) =>
    p.fullWidth &&
    `
    width: 100%;
  `}
`

const StyledButtonGold = styled(StyledButton)`
  border: 1px solid transparent;
  background: #997f45;
  box-shadow: 0px 1px 0px 0px rgba(0, 0, 0, 0.05),
    0px 4px 4px 0px rgba(0, 0, 0, 0.05), 0px 10px 10px 0px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(7px);

  ${(p) =>
    !p.disabled &&
    `
    &:hover {
      background: #b39558;
      box-shadow:
        0px 1px 0px 0px rgba(0, 0, 0, 0.05),
        0px 4px 4px 0px rgba(0, 0, 0, 0.05),
        0px 10px 10px 0px rgba(0, 0, 0, 0.1),
        0 0 15px 0 rgba(255, 196, 69, 0.4);
    }
  `}
`

const StyledYellowBtn = styled(StyledButton)`
  background: ${(p) => p.theme.rpTheme.colors.gold1};
  box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.05), 0 4px 4px 0 rgba(0, 0, 0, 0.05),
    0 10px 10px 0 rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(7px);

  ${(p) =>
    !p.disabled &&
    `
    &:hover {
      background: ${p.theme.rpTheme.colors.gold1};
      filter: brightness(1.1);
    }
  `}
`

export const Btn: FC<BtnProps> = memo(
  ({
    txt = '',
    onClick,
    variant = 'default',
    fullWidth = false,
    disabled = false,
    children,
    ...rest
  }) => {
    // стабильный хендлер, чтобы button.disabled и html-атрибут disabled
    // сразу блокировали клики
    const handleClick = (): void => {
      if (!disabled) {
        onClick?.()
      }
    }

    return (
      <StyledButton
        type="button"
        variant={variant}
        fullWidth={fullWidth}
        disabled={disabled}
        onClick={handleClick}
        {...rest}>
        {txt}
        {children}
      </StyledButton>
    )
  }
)

export const BtnGold: FC<BtnGoldProps> = memo(
  ({
    txt = '',
    onClick,
    fullWidth = false,
    disabled = false,
    children,
    ...rest
  }) => {
    const handleClick = (): void => {
      if (!disabled) {
        onClick?.()
      }
    }

    return (
      <StyledButtonGold
        type="button"
        variant="default"
        fullWidth={fullWidth}
        disabled={disabled}
        onClick={handleClick}
        {...rest}>
        {txt}
        {children}
      </StyledButtonGold>
    )
  }
)

export const BtnYellow: FC<BtnGoldProps> = memo(
  ({
    txt = '',
    onClick,
    fullWidth = false,
    disabled = false,
    children,
    ...rest
  }) => {
    const handleClick = (): void => {
      if (!disabled) {
        onClick?.()
      }
    }

    return (
      <StyledYellowBtn
        type="button"
        variant="default"
        fullWidth={fullWidth}
        disabled={disabled}
        onClick={handleClick}
        {...rest}>
        {txt}
        {children}
      </StyledYellowBtn>
    )
  }
)
